import { toPlainText } from "@rhema/format";
import { io, type Socket } from "socket.io-client";
import { albumOf, albumPreview } from "./album";
import { type Api, isPlanLimit } from "./api";
import { type ActiveCall, CallManager } from "./calls";
import type { AlbumMark, Content, MediaRef, TextFormat } from "./content";
import { DraftStore } from "./drafts";
import {
  type BackupStatus,
  type CodeRecovery,
  CryptoEngine,
  type PinRecovery,
  type SignOutCheck,
} from "./e2ee";
import { type FileMeta, MAX_MEDIA_BYTES, MediaClient, type MediaOptions, MediaTooLargeError } from "./media";
import { ChunkedUploader } from "./multipart";
import type { Store } from "./storage";
import type {
  CallInfo,
  CallMedia,
  ChannelInfo,
  Chat,
  ChatPreferences,
  Device,
  ForwardedFile,
  MessageContext,
  MessageInfo,
  QuietHours,
  ServerMessage,
  ServerScheduled,
  ServerWorkflow,
  SmsInvite,
  Space,
  SpaceNotifications,
  User,
  WorkflowSchedule,
} from "./types";
import {
  type FileSpool,
  MemorySpool,
  mediaRunner,
  type QueuedUpload,
  UploadQueue,
  type UploadView,
  uploadContent,
} from "./uploadQueue";
import { parseContent, sanitizeMessages } from "./validate";

export type Delivery = "sending" | "sent" | "delivered" | "read" | "failed";

export type ChatMessage = {
  id: string;
  clientId: string | null;
  chatId: string;
  seq: number;
  senderId: string | null;
  createdAt: string;
  kind: "message" | "system";
  content: Content | null;
  /** Why the content is missing, when it is. */
  problem?: "missing_key" | "lost" | "invalid" | "replay" | "deleted" | "malformed";
  system: Record<string, string> | null;
  edited: string | null;
  /** How the edited body is written, when the edit carried a format. */
  editedFormat?: TextFormat;
  reactions: Record<string, string[]>;
  deleted: boolean;
  status: Delivery;
  expiresAt: string | null;
  /** Upload progress from 0 to 1, while a file you sent is uploading. */
  progress?: number;
  /** The file you are sending, shown while it uploads. Never stored. */
  localFile?: Blob;
  /** A reply in a thread names the message it replies to. */
  threadRoot?: string | null;
  /** On a message with a thread: how many replies, and when the last came. */
  replyCount?: number;
  lastReplyAt?: string | null;
  /** On a forward you are sending: the files it carries from the message it came from. */
  forwardedFiles?: ForwardedFile[];
};

/** A starred message, decrypted on this device. */
export type Starred = { chatId: string; starredAt: string; message: ChatMessage };

/** The page around a message a link points to, decrypted on this device. */
export type MessagePage = Omit<MessageContext, "messages"> & { messages: ChatMessage[] };

/**
 * Where a thread's replies are kept in `messages`, beside the chat's own timeline. A thread's
 * replies stay out of the timeline, which shows each thread's first message with its count.
 */
export function threadKey(chatId: string, rootId: string): string {
  return `${chatId}#${rootId}`;
}

/** A pinned message, decrypted. */
export type Pinned = { message: ChatMessage; pinnedBy: string | null; pinnedAt: string };

/**
 * A message waiting to be posted. The server holds it encrypted. Its text is kept on the device
 * that scheduled it, so other devices show it without a preview.
 */
export type Scheduled = {
  id: string;
  chatId: string;
  clientId: string;
  sendAt: string;
  threadRoot: string | null;
  content: Content | null;
};

/** A space workflow with its text, decrypted on this device when it can be. */
export type Workflow = {
  id: string;
  spaceId: string;
  chatId: string;
  kind: "welcome" | "recurring";
  name: string;
  ownerId: string;
  /** Null when this device cannot read it, because it was not in the channel when it was written. */
  text: string | null;
  repeat: "daily" | "weekdays" | "weekly" | null;
  weekday: number | null;
  minute: number | null;
  tz: string;
  paused: boolean;
  problem: string | null;
  nextRunAt: string | null;
  queued: number;
  queuedUntil: string | null;
  waiting: number;
  lastRunAt: string | null;
};

/** What an admin writes when making or editing a workflow. */
export type WorkflowInput = {
  chatId: string;
  kind: "welcome" | "recurring";
  name: string;
  text: string;
  schedule?: WorkflowSchedule;
};

/** The other apps a space's admins can turn off for its channels. */
export type AppName = "queravel" | "malakh" | "penielly";

/**
 * Whether Queravel, Malakh or Penielly may be used in a chat: a group's admins can turn Queravel
 * off for the group, and a space's admins can turn any of them off for its channels.
 */
export function appOn(state: Pick<ClientState, "spaces">, chat: Chat, app: AppName): boolean {
  if (chat.kind === "assistant") return false;
  if (app === "queravel" && chat.assistant_allowed === false) return false;
  if (chat.kind === "channel" && chat.space_id) {
    // A channel shared between spaces follows the strictest of those this person can see.
    const ids = [chat.space_id, ...(chat.shared_with ?? []).map((s) => s.id)];
    if (state.spaces.some((s) => ids.includes(s.id) && s.apps?.[app] === false)) return false;
  }
  return true;
}

/** A reminder about a message, for the Later list. */
export type Reminder = {
  id: string;
  chatId: string;
  message: ChatMessage;
  remindAt: string;
  notified: boolean;
  done: boolean;
};

/** A message found by search, on this device. */
export type SearchHit = { chatId: string; message: ChatMessage; text: string };

/** The words a message can be found by: its text, caption, file name or poll question. */
export function searchableText(m: ChatMessage): string {
  if (m.deleted || !m.content) return "";
  // Formatting marks are left out so a search for words finds them, and link addresses stay in.
  if (typeof m.edited === "string" && m.edited)
    return toPlainText(m.edited, m.editedFormat, { withUrls: true });
  // Checked again, so a message kept from an older version cannot break search.
  const c = parseContent(m.content);
  if (!c) return "";
  switch (c.type) {
    case "text":
      return toPlainText(c.body, c.format, { withUrls: true });
    case "queravel":
      return c.body;
    case "media":
      return [c.caption, c.media.name].filter(Boolean).join(" ");
    case "poll":
      return [c.question, ...c.options].join(" ");
    case "contact":
      return c.name;
    default:
      return "";
  }
}

export type SendMediaOptions = {
  kind: "photo" | "video" | "file" | "voice";
  caption?: string;
  reply_to?: string;
  /** Marks photos and videos picked together, so they show as one album. */
  album?: AlbumMark;
  /** People @mentioned in the caption. */
  mentions?: string[];
  /** Dimensions, duration, waveform and thumbnail, when the app worked them out. */
  meta?: FileMeta;
  /** Sends it as a reply in this thread. */
  threadRoot?: string;
};

export type ClientState = {
  me: User | null;
  connection: "starting" | "online" | "offline";
  chats: Record<string, Chat>;
  chatOrder: string[];
  previews: Record<string, string>;
  messages: Record<string, ChatMessage[]>;
  hasOlder: Record<string, boolean>;
  typing: Record<string, string[]>;
  presence: Record<string, { online: boolean; lastSeen: string | null }>;
  /** The spaces you are in, oldest first. Their channels are chats with a space_id. */
  spaces: Space[];
  /** Pinned messages, newest pin first, for each chat whose pins have been loaded. */
  pins: Record<string, Pinned[]>;
  /** Your scheduled messages, soonest first, or null before they are loaded. */
  scheduled: Scheduled[] | null;
  /** Your open reminders, soonest first, or null before they are loaded. */
  reminders: Reminder[] | null;
  /** The invites the server texted for you in the last month, newest first, or null before they are loaded. */
  smsInvites: SmsInvite[] | null;
  /** Each space's workflows, for its admins, once loaded. */
  workflows: Record<string, Workflow[]>;
  /** The ids of the messages you starred, for each chat whose stars have been loaded. */
  stars?: Record<string, string[]>;
};

export type ClientOptions = {
  api: Api;
  store: Store;
  deviceName: string;
  platform: Device["platform"];
  /** Socket.IO origin. Empty for the web app. */
  socketUrl?: string;
  socketToken?: () => string | null;
  media?: MediaOptions;
  /**
   * Where files waiting to upload are kept, so sending carries on after a restart: IndexedDB on
   * the web and phones, the app data folder on desktop. Without one they are kept in memory.
   */
  uploadSpool?: FileSpool;
  /** Supplies the pickle key from the OS keychain on desktop and mobile. The web app leaves it out. */
  pickleKey?: () => Promise<Uint8Array>;
  /**
   * Older stores on this device whose keys this client may import, such as one kept under an
   * account that was merged into this one, with the pickle key that opens each.
   */
  oldStores?: () => Promise<{ id: string; store: Store; pickleKey: Uint8Array }[]>;
  /** The operator recovery key's fingerprint, built into the app. See CryptoOptions. */
  operatorKeyFingerprint?: string;
};

/** What the person's backup heard from the server: its state changed, or support started a recovery. */
export type BackupEvent = { kind: "changed" } | { kind: "recovery_started"; expiresAt: string | null };

type Pending = {
  chatId: string;
  clientId: string;
  content: Content;
  kind: "message" | "reaction" | "edit" | "vote" | "notice";
  relatesTo?: string;
  /** Kept with the message so a retry or a later flush still notifies the people mentioned. */
  mentions?: string[];
  attachments?: string[];
  /** Files a forward carries from the message it came from, without a second upload. */
  forwarded?: ForwardedFile[];
  /** For a reply in a thread, or a reaction or edit to one: the thread's first message. */
  threadRoot?: string;
};

/** The list in `messages` a pending message shows in. */
function listOf(p: Pick<Pending, "chatId" | "threadRoot">): string {
  return p.threadRoot ? threadKey(p.chatId, p.threadRoot) : p.chatId;
}

const TYPING_TTL = 6000;
/** How many files upload at the same time. */
export const UPLOADS_AT_ONCE = 2;

const PAGE = 50;
const CACHE_LIMIT = 400;
/** How long delivery acks wait to go out together, and the most chats one request carries. */
const ACK_DELAY = 1500;
const ACK_BATCH = 500;

/** The named fields of an object, for putting some of them back. */
function pick<T extends object>(from: T, keys: (keyof T)[]): Partial<T> {
  const out: Partial<T> = {};
  for (const k of keys) if (k in from) out[k] = from[k];
  return out;
}

function uuid(): string {
  return crypto.randomUUID();
}

/**
 * Content sent as a notice: a line such as "Ama asked Queravel about this chat", or a Queravel
 * conversation's title. The server never pushes a notice or counts it as unread.
 */
function isNotice(content: Content): boolean {
  return content.type === "notice" || content.type === "queravel_title";
}

/** What a chat list or notification says for a message that could not be shown. */
export const MALFORMED_PREVIEW = "This message could not be shown";

/** Describes message content in one line, for chat list previews and notifications. */
export function previewOf(raw: Content | null, problem?: string): string {
  // Checked again, so a preview never shows or throws on a field of the wrong type.
  const content = raw === null ? null : parseContent(raw);
  if (!content) {
    if (problem === "deleted") return "Message deleted";
    if (raw !== null || problem === "malformed") return MALFORMED_PREVIEW;
    return problem === "lost" ? "Message can't be recovered" : "Waiting for this message";
  }
  switch (content.type) {
    case "text":
      return toPlainText(content.body, content.format);
    case "media":
      return (
        content.caption ||
        { photo: "Photo", video: "Video", file: content.media.name || "File", voice: "Voice message" }[
          content.kind
        ]
      );
    case "location":
      return content.live_until ? "Live location" : "Location";
    case "contact":
      return `Contact: ${content.name}`;
    case "poll":
      return `Poll: ${content.question}`;
    case "vote":
      return "Voted in a poll";
    case "queravel":
      return content.body;
    case "notice":
      return "Asked Queravel about this chat";
    case "imported":
      return "Imported messages from WhatsApp";
    case "call":
      return content.event === "missed"
        ? "Missed call"
        : content.media === "video"
          ? "Video call"
          : "Voice call";
    default:
      return "";
  }
}

export class RhemaClient {
  readonly api: Api;
  /** Call state, with its own subscription so call updates do not re-render the chat screens. */
  readonly calls: CallManager;
  readonly media: MediaClient;
  /** What was being written in each chat and thread, kept on this device. */
  readonly drafts: DraftStore;
  crypto!: CryptoEngine;
  /**
   * Files waiting to upload, and the one place that decides how many upload at once (two), the
   * order their messages post in, cancelling and retrying. It keeps them on the device, so
   * sending carries on after a restart.
   */
  readonly uploads: UploadQueue;
  private uploadsStarted: Promise<void> | null = null;
  /** sendMedia and retry callers waiting for a file to post, fail or be cancelled. */
  private uploadWaiters = new Map<string, { resolve: () => void; reject: (err: unknown) => void }[]>();
  /** The error behind each failed upload, so a full storage plan reaches the screen. */
  private uploadErrors = new Map<string, unknown>();
  /** The progress last drawn for each upload, so the screen redraws at most once a percent. */
  private uploadShown = new Map<string, number>();
  private store: Store;
  private socket: Socket | null = null;
  private listeners = new Set<() => void>();
  private mergeListeners = new Set<(survivor: string) => void>();
  private backupListeners = new Set<(e: BackupEvent) => void>();
  private typingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private lastTypingSent = new Map<string, number>();
  private syncing = new Map<string, Promise<void>>();
  /** Delivery acks waiting to go out, the newest seq for each chat. */
  private acks = new Map<string, number>();
  private ackTimer: ReturnType<typeof setTimeout> | null = null;
  private opts: ClientOptions;
  state: ClientState = {
    me: null,
    connection: "starting",
    chats: {},
    chatOrder: [],
    previews: {},
    messages: {},
    hasOlder: {},
    typing: {},
    presence: {},
    spaces: [],
    pins: {},
    scheduled: null,
    reminders: null,
    smsInvites: null,
    workflows: {},
  };

  constructor(opts: ClientOptions) {
    this.opts = opts;
    this.api = opts.api;
    this.store = opts.store;
    this.drafts = new DraftStore(opts.store);
    this.calls = new CallManager({
      api: this.api,
      meId: () => this.state.me?.id,
      send: (chatId, content) => this.send(chatId, content),
      messages: (chatId) => this.state.messages[chatId],
      // The key message may have arrived before its Megolm session did, so pick up new room keys
      // and retry anything undecrypted as well as fetching new messages.
      sync: async (chatId) => {
        await this.openChat(chatId);
        if (this.crypto) await this.onKeys().catch(() => undefined);
        await this.retryUndecrypted(chatId).catch(() => undefined);
      },
    });
    this.media = new MediaClient(opts.api, opts.media);
    const runner = mediaRunner({
      media: this.media,
      chunked: new ChunkedUploader(this.api),
      deliver: (item, ref) => this.postUpload(item, ref),
    });
    this.uploads = new UploadQueue(
      this.store,
      opts.uploadSpool ?? new MemorySpool(),
      {
        ...runner,
        upload: async (item, source, ctx) => {
          try {
            return await runner.upload(item, source, ctx);
          } catch (err) {
            this.uploadErrors.set(item.id, err);
            throw err;
          }
        },
      },
      // A failed file waits for the person to retry it. Large files already retry each part.
      { concurrency: UPLOADS_AT_ONCE, autoRetries: 1 },
    );
    this.uploads.subscribe((items) => this.showUploads(items));
  }

  // Subscription for React's useSyncExternalStore.

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = () => this.state;

  private set(patch: Partial<ClientState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  // Lifecycle

  async start(me?: User): Promise<void> {
    const user = me ?? (await this.api.me());
    this.set({ me: user });
    this.crypto = new CryptoEngine(this.api, this.store, user.id, this.opts.pickleKey, {
      onRestored: (chats) => void this.retryChats(chats),
      operatorKeyFingerprint: this.opts.operatorKeyFingerprint,
    });
    await this.crypto.start(this.opts.deviceName, this.opts.platform);
    await this.importOldStores();
    const cachedPreviews = (await this.store.get<Record<string, string>>("previews")) ?? {};
    this.set({ previews: cachedPreviews });
    // The chat list kept from last time shows at once. Keys and chats are brought up to date in
    // the background, so a returning launch does not wait on the network.
    const cachedChats = await this.store.get<Chat[]>("chat-list");
    if (cachedChats?.length && !this.state.chatOrder.length) {
      const chats: Record<string, Chat> = {};
      for (const c of cachedChats) chats[c.id] = c;
      this.set({ chats, chatOrder: cachedChats.map((c) => c.id) });
    }
    this.connect();
    void this.catchUp().catch(() => undefined);
    void this.flushOutbox();
    void this.startUploads().catch(() => undefined);
    void this.shareKeys(true);
    void this.syncBackup();
  }

  /** Whether this device has no keys yet, so starting will create and register them. */
  async isNewDevice(): Promise<boolean> {
    return (await this.store.get<string>("account")) === undefined;
  }

  private catching: Promise<void> | null = null;

  /**
   * Applies key messages that came in while away, then refreshes the chats. Calls made while one
   * is running share it, so starting and the socket connecting do not both do the work.
   */
  private catchUp(): Promise<void> {
    if (!this.catching) {
      this.catching = (async () => {
        await this.onKeys();
        await this.refreshChats();
      })().finally(() => {
        this.catching = null;
      });
    }
    return this.catching;
  }

  /**
   * Imports the message keys of older stores on this device, such as the one kept under an
   * account merged into this one, while their pickle keys are still here. They go into this
   * device's store and from there into the backup.
   */
  private async importOldStores(): Promise<void> {
    try {
      for (const old of (await this.opts.oldStores?.()) ?? [])
        await this.crypto.importSessionsFrom(old.store, old.pickleKey, old.id).catch(() => undefined);
    } catch {
      // Nothing to import, or the stores cannot be opened.
    }
  }

  /** Brings the encrypted key backup up to date and shows what it brought in. */
  private async syncBackup(): Promise<void> {
    try {
      await this.retryChats(await this.crypto.syncBackup());
    } catch {
      // Offline. The next start, key or event tries again.
    }
  }

  private async retryChats(chats: string[]): Promise<void> {
    for (const chatId of chats) await this.retryUndecrypted(chatId).catch(() => undefined);
  }

  /**
   * Backs up this device's message keys before it signs out, waiting at most `timeoutMs`, and
   * says whether signing out would leave messages that only this device can read.
   */
  prepareSignOut(timeoutMs = 10_000): Promise<SignOutCheck> {
    return this.crypto.signOutCheck(timeoutMs);
  }

  backupStatus(): Promise<BackupStatus> {
    return this.crypto.backupStatus();
  }

  /** Sets or changes the backup PIN that can bring the backup key back on a new device. */
  setBackupPin(pin: string): Promise<void> {
    return this.crypto.setBackupPin(pin);
  }

  /** Remembers that the person chose not to set a backup PIN now. */
  skipBackupPin(): Promise<void> {
    return this.crypto.markPinAsked();
  }

  /** Recovers the backup key with the backup PIN and shows the history it unlocks. */
  async recoverWithPin(pin: string): Promise<PinRecovery> {
    const result = await this.crypto.recoverWithPin(pin);
    if (result.ok) await this.retryChats(result.chats);
    return result;
  }

  /** Recovers the backup key with a recovery code from Rhema support and shows the history. */
  async recoverWithCode(code: string): Promise<CodeRecovery> {
    const result = await this.crypto.recoverWithCode(code);
    if (result.ok) await this.retryChats(result.chats);
    return result;
  }

  /** Turns advanced protection on, which stops Rhema support from ever recovering the history, or off. */
  setAdvancedProtection(enabled: boolean): Promise<void> {
    return this.crypto.setAdvancedProtection(enabled);
  }

  /** Called when the backup's state changes on the server, or Rhema support starts a recovery. */
  onBackupEvent(listener: (e: BackupEvent) => void): () => void {
    this.backupListeners.add(listener);
    return () => this.backupListeners.delete(listener);
  }

  /**
   * Keeps this person's devices able to read the same history: sends what this device holds to any
   * of their devices it has not seen, and asks again for the keys this device is still waiting for.
   */
  private async shareKeys(askAgain: boolean): Promise<void> {
    try {
      await this.crypto.shareWithNewOwnDevices();
      if (askAgain) await this.crypto.requestKeys({ force: true });
    } catch {
      // Offline or not ready. The next start, device change or presence tries again.
    }
  }

  stop(): void {
    this.crypto?.stop?.();
    this.socket?.disconnect();
    this.socket = null;
    this.listeners.clear();
    this.mergeListeners.clear();
    this.backupListeners.clear();
    this.calls.dispose();
  }

  private connect() {
    const token = this.opts.socketToken?.();
    const socket = io(this.opts.socketUrl ?? "", {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      withCredentials: !this.opts.socketUrl,
      auth: token ? { token } : {},
    });
    this.socket = socket;
    this.calls.attach(socket);
    socket.on("connect", () => {
      this.set({ connection: "online" });
      // Catch up on anything missed while offline, including workflow work.
      void this.catchUp()
        .then(() => this.runWorkflows())
        .catch(() => undefined);
      void this.flushOutbox();
    });
    socket.on("disconnect", () => this.set({ connection: "offline" }));
    socket.on("message.new", (m: ServerMessage) => {
      // The message reached this device, so its sender learns it was delivered.
      if (m.sender_id !== this.state.me?.id) this.queueDelivered(m.chat_id, m.seq);
      // A reply only matters here when its thread is open. Its count comes by thread.updated.
      if (m.thread_root) {
        if (this.state.messages[threadKey(m.chat_id, m.thread_root)])
          void this.syncThread(m.chat_id, m.thread_root);
        return;
      }
      void this.syncChat(m.chat_id);
    });
    socket.on("space.updated", (e: { space_id: string; deleted?: boolean; notifications?: boolean }) => {
      // New notifications for a space change what its channels show too.
      if (e.deleted || e.notifications) void this.refreshChats();
      else void this.refreshSpaces();
    });
    // Delete for me, from another of your devices.
    socket.on("messages.hidden", (e: { chat_id: string; message_ids: string[] }) =>
      this.dropMessages(e.chat_id, e.message_ids),
    );
    socket.on("stars.updated", (e: { chat_id: string; message_id: string; starred: boolean }) =>
      this.applyStar(e.chat_id, e.message_id, e.starred),
    );
    // Your own settings changed on another device.
    socket.on("me.updated", () => {
      void this.api
        .me()
        .then((me) => this.set({ me }))
        .catch(() => undefined);
    });
    socket.on(
      "thread.updated",
      (t: { chat_id: string; message_id: string; reply_count: number; last_reply_at: string | null }) =>
        this.applyThreadCount(t.chat_id, t.message_id, t.reply_count, t.last_reply_at),
    );
    socket.on("pins.updated", (e: { chat_id: string }) => {
      if (this.state.pins[e.chat_id]) void this.loadPins(e.chat_id).catch(() => undefined);
    });
    socket.on("scheduled.updated", (e: { chat_id: string; sent?: string }) => {
      if (this.state.scheduled) void this.loadScheduled().catch(() => undefined);
      if (e.sent) void this.store.delete(`scheduled:${e.sent}`);
    });
    socket.on("reminders.updated", () => {
      if (this.state.reminders) void this.loadReminders().catch(() => undefined);
    });
    // A texted invite was sent, delivered or not delivered.
    socket.on("invites.updated", () => {
      if (this.state.smsInvites) void this.loadSmsInvites().catch(() => undefined);
    });
    socket.on("workflows.updated", (e: { space_id: string }) => {
      if (this.state.workflows[e.space_id]) void this.loadWorkflows(e.space_id).catch(() => undefined);
    });
    // A workflow this person wrote needs this device: to welcome someone or encrypt its next runs.
    socket.on("workflows.due", () => void this.runWorkflows());
    socket.on("user.updated", (e: { user_id: string }) => void this.refreshUser(e.user_id));
    socket.on("chat.updated", (e: { chat_id: string; members?: boolean }) => {
      if (e.members) this.crypto.forgetDevices();
      void this.refreshChat(e.chat_id);
    });
    socket.on("receipt", (r: { chat_id: string; user_id: string; read_seq: number; delivered_seq: number }) =>
      this.applyReceipt(r),
    );
    socket.on("typing", (t: { chat_id: string; user_id: string; active: boolean }) => this.applyTyping(t));
    socket.on("presence", (p: { user_id: string; online: boolean; last_seen?: string }) => {
      this.set({
        presence: {
          ...this.state.presence,
          [p.user_id]: { online: p.online, lastSeen: p.last_seen ?? null },
        },
      });
      // A device that may hold a missing key is back. Ask it again.
      if (p.online) {
        void this.crypto.requestKeys({ force: true }).catch(() => undefined);
        if (p.user_id === this.state.me?.id) void this.crypto.askForBackupKeyAgain().catch(() => undefined);
      }
    });
    socket.on("to_device", () => void this.onKeys());
    socket.on("devices.changed", (e: { user_id: string }) => {
      this.crypto.forgetDevices(e.user_id);
      // A new device of this person gets the history this device holds, and any device that changed
      // may hold a key this one is waiting for.
      if (e.user_id === this.state.me?.id) {
        void this.shareKeys(true);
        void this.crypto.askForBackupKeyAgain().catch(() => undefined);
      } else void this.crypto.requestKeys({ force: true }).catch(() => undefined);
    });
    // A new backup key replaced the old one. Fetch it and upload again under it.
    socket.on("key_backup.changed", () => void this.syncBackup());
    // Advanced protection changed, or a recovery code was issued or used.
    socket.on("key_escrow.changed", () => {
      void this.crypto.escrowChanged();
      for (const listener of this.backupListeners) listener({ kind: "changed" });
    });
    socket.on("backup.recovery_started", (e: { expires_at?: string }) => {
      for (const listener of this.backupListeners)
        listener({ kind: "recovery_started", expiresAt: e?.expires_at ?? null });
    });
    socket.on("account.merged", (e: { user_id: string; merged: string }) => void this.onMerged(e.user_id));
    const heartbeat = setInterval(() => socket.connected && socket.emit("heartbeat"), 30_000);
    socket.on("disconnect", () => undefined);
    socket.io.on("close", () => clearInterval(heartbeat));
  }

  /**
   * Another account of this person was combined into this one, or this one into an older one.
   * Everything is fetched again. A device whose account was the one combined away is now signed in
   * as the survivor, which the app hears through `onAccountMerged` and starts again for.
   */
  private async onMerged(survivor: string): Promise<void> {
    this.crypto.forgetDevices();
    if (survivor !== this.state.me?.id) {
      for (const listener of this.mergeListeners) listener(survivor);
      return;
    }
    const me = await this.api.me().catch(() => null);
    if (me) this.set({ me });
    await this.refreshChats().catch(() => undefined);
    void this.shareKeys(true);
    // The merged account's backup came across. Read it all again.
    await this.crypto.resetBackupCursor().catch(() => undefined);
    void this.syncBackup();
    for (const listener of this.mergeListeners) listener(survivor);
  }

  /** Called with the surviving account's id when this person's accounts are combined. */
  onAccountMerged(listener: (survivor: string) => void): () => void {
    this.mergeListeners.add(listener);
    return () => this.mergeListeners.delete(listener);
  }

  private async onKeys() {
    const chats = await this.crypto.processToDevice();
    for (const chatId of chats) await this.retryUndecrypted(chatId);
  }

  // Chats

  async refreshSpaces(): Promise<void> {
    this.set({ spaces: await this.api.spaces() });
  }

  async createSpace(name: string, about = ""): Promise<Space> {
    const space = await this.api.createSpace(name, about);
    await Promise.all([this.refreshSpaces(), this.refreshChats()]);
    return space;
  }

  async joinSpace(code: string): Promise<Space> {
    const space = await this.api.joinSpace(code);
    await Promise.all([this.refreshSpaces(), this.refreshChats()]);
    return space;
  }

  async leaveSpace(spaceId: string, deleting = false): Promise<void> {
    if (deleting) await this.api.deleteSpace(spaceId);
    else await this.api.leaveSpace(spaceId);
    await Promise.all([this.refreshSpaces(), this.refreshChats()]);
  }

  async createChannel(
    spaceId: string,
    body: { name: string; topic?: string; private?: boolean; announcement?: boolean; everyone?: boolean },
  ): Promise<Chat> {
    const chat = await this.api.createChannel(spaceId, body);
    this.putChat(chat);
    return chat;
  }

  async joinChannel(spaceId: string, chatId: string): Promise<Chat> {
    const chat = await this.api.joinChannel(spaceId, chatId);
    this.putChat(chat);
    return chat;
  }

  async leaveChannel(spaceId: string, chatId: string): Promise<void> {
    await this.api.leaveChannel(spaceId, chatId);
    // People who leave must not be able to read what comes next.
    await this.crypto.rotate(chatId).catch(() => undefined);
    await this.refreshChats();
  }

  /** Takes someone out of a channel, then rotates its keys so they cannot read what comes next. */
  async removeFromChannel(spaceId: string, chatId: string, userId: string): Promise<void> {
    await this.api.removeFromChannel(spaceId, chatId, userId);
    await this.crypto.rotate(chatId);
    await this.refreshChat(chatId);
  }

  /** Your notifications for a space. Channels that follow the space show the change too. */
  async setSpaceNotifications(
    spaceId: string,
    body: { mentions_only?: boolean; muted_until?: string | null },
  ): Promise<SpaceNotifications> {
    const result = await this.api.setSpaceNotifications(spaceId, body);
    this.set({ spaces: this.state.spaces.map((s) => (s.id === spaceId ? { ...s, ...result } : s)) });
    await this.refreshChats();
    return result;
  }

  /** Mark all as read for a space, in one request. */
  async markSpaceRead(spaceId: string): Promise<void> {
    this.markReadLocally((await this.api.markSpaceRead(spaceId)).chat_ids);
  }

  /** Mark all as read for every archived chat, in one request. */
  async markArchivedRead(): Promise<void> {
    this.markReadLocally((await this.api.markArchivedRead()).chat_ids);
  }

  private markReadLocally(chatIds: string[]) {
    if (!chatIds.length) return;
    const chats = { ...this.state.chats };
    for (const id of chatIds) {
      const chat = chats[id];
      if (chat)
        chats[id] = { ...chat, unread: 0, mentioned: false, marked_unread: false, read_seq: chat.last_seq };
    }
    this.set({ chats });
  }

  /** Whether archived chats stay archived when a new message comes in. */
  async setKeepArchived(keep: boolean): Promise<ChatPreferences> {
    const result = await this.api.setChatPreferences({ keep_archived: keep });
    if (this.state.me) this.set({ me: { ...this.state.me, keep_archived: result.keep_archived } });
    return result;
  }

  /** Stars or unstars a chat or channel. */
  async starChat(chatId: string, starred = true): Promise<void> {
    this.putChat(await this.api.updateChatSettings(chatId, { starred }));
  }

  async refreshChats(): Promise<void> {
    void this.refreshSpaces().catch(() => undefined);
    const list = await this.api.chats();
    const chats: Record<string, Chat> = {};
    for (const c of list) chats[c.id] = c;
    this.set({ chats, chatOrder: list.map((c) => c.id) });
    this.ackChats(list);
    void this.store.set("chat-list", list);
    await Promise.all(list.map((c) => this.updatePreview(c)));
    const peers = list.filter((c) => c.kind === "direct").flatMap((c) => c.members.map((m) => m.user.id));
    const others = [...new Set(peers)].filter((id) => id !== this.state.me?.id);
    if (others.length) {
      const presence = await this.api.presence(others).catch(() => ({}));
      const next = { ...this.state.presence };
      for (const [id, p] of Object.entries(presence)) next[id] = { online: p.online, lastSeen: p.last_seen };
      this.set({ presence: next });
    }
    for (const chatId of Object.keys(this.state.messages)) void this.syncChat(chatId);
  }

  async refreshChat(chatId: string): Promise<Chat | null> {
    try {
      const chat = await this.api.chat(chatId);
      this.putChat(chat);
      await this.updatePreview(chat);
      return chat;
    } catch (err) {
      // Only a 404 means we are no longer in the chat. Being offline must not empty the list.
      if ((err as { status?: number }).status !== 404) return this.state.chats[chatId] ?? null;
      const { [chatId]: _gone, ...rest } = this.state.chats;
      this.set({ chats: rest, chatOrder: this.state.chatOrder.filter((id) => id !== chatId) });
      return null;
    }
  }

  /** Drops cached messages a chat's clear left behind, on this device and in its store. */
  private prune(chat: Chat) {
    const cleared = chat.cleared_seq ?? 0;
    const list = this.state.messages[chat.id];
    if (!cleared || !list?.some((m) => m.seq > 0 && m.seq <= cleared)) return;
    this.setMessages(
      chat.id,
      list.filter((m) => m.seq === 0 || m.seq > cleared),
    );
  }

  /**
   * Clear messages, or Delete chat. It happens on every device of yours and for nobody else,
   * since everyone's copy is encrypted on their own devices.
   */
  async clearChat(chatId: string, deleting = false): Promise<void> {
    const chat = await this.api.clearChat(chatId, deleting);
    this.setMessages(chatId, []);
    const { [chatId]: _cleared, ...previews } = this.state.previews;
    this.set({ previews });
    void this.store.set("previews", previews);
    if (deleting) {
      const { [chatId]: _c, ...chats } = this.state.chats;
      this.set({ chats, chatOrder: this.state.chatOrder.filter((id) => id !== chatId) });
    } else this.putChat(chat);
  }

  /** Mark as unread, or read again. */
  async markUnread(chatId: string, unread = true): Promise<void> {
    this.putChat(await this.api.updateChatSettings(chatId, { marked_unread: unread }));
  }

  /** Mark unread from here: your read point moves back to just before this message. */
  async markUnreadFrom(chatId: string, messageId: string): Promise<void> {
    this.putChat(await this.api.markUnreadFrom(chatId, messageId));
  }

  /** Delete for me: the messages leave this device and your others, and stay for everyone else. */
  async hideMessages(chatId: string, ids: string[]): Promise<void> {
    await this.api.hideMessages(chatId, ids);
    this.dropMessages(chatId, ids);
  }

  /** Takes messages off this device: from the chat, its open threads and its stars. */
  private dropMessages(chatId: string, ids: string[]) {
    const gone = new Set(ids);
    for (const [key, list] of Object.entries(this.state.messages)) {
      if (key !== chatId && !key.startsWith(`${chatId}#`)) continue;
      if (list.some((m) => gone.has(m.id)))
        this.setMessages(
          key,
          list.filter((m) => !gone.has(m.id)),
        );
    }
    const stars = this.state.stars?.[chatId];
    if (stars?.some((id) => gone.has(id)))
      this.set({ stars: { ...this.state.stars, [chatId]: stars.filter((id) => !gone.has(id)) } });
    void this.refreshChat(chatId);
  }

  /** The messages you starred in a chat, kept up to date once loaded. */
  async loadStars(chatId: string): Promise<string[]> {
    const ids = await this.api.chatStars(chatId);
    this.set({ stars: { ...this.state.stars, [chatId]: ids } });
    return ids;
  }

  async starMessage(chatId: string, messageId: string, starred = true): Promise<void> {
    if (starred) await this.api.starMessage(chatId, messageId);
    else await this.api.unstarMessage(chatId, messageId);
    this.applyStar(chatId, messageId, starred);
  }

  private applyStar(chatId: string, messageId: string, starred: boolean) {
    const current = this.state.stars?.[chatId];
    if (!current) return;
    const rest = current.filter((id) => id !== messageId);
    this.set({ stars: { ...this.state.stars, [chatId]: starred ? [messageId, ...rest] : rest } });
  }

  /** A page of your starred messages across chats, newest star first, decrypted here. */
  async starred(before?: string): Promise<{ items: Starred[]; next: string | null }> {
    const page = await this.api.starredMessages(before ? { before } : {});
    const items = await Promise.all(
      page.items.map(async (s) => ({
        chatId: s.chat_id,
        starredAt: s.starred_at,
        message: await this.decode(s.message),
      })),
    );
    return { items, next: page.next };
  }

  /** Opens a link to a message: the page around it, decrypted here. Rejects with 404 for anyone not in the chat. */
  async messagePage(chatId: string, messageId: string, limit?: number): Promise<MessagePage> {
    const page = await this.api.messageContext(chatId, messageId, limit);
    const messages: ChatMessage[] = [];
    for (const raw of page.messages)
      if (["message", "system", "vote", "notice", "import"].includes(raw.kind))
        messages.push(await this.decode(raw));
    return { ...page, messages };
  }

  /** When each person got and read a message you sent. */
  messageInfo(chatId: string, messageId: string): Promise<MessageInfo> {
    return this.api.messageInfo(chatId, messageId);
  }

  /** Deletes messages of your own chats for good, such as one Queravel conversation. */
  async deleteMessages(chatId: string, ids: string[]): Promise<void> {
    const gone = new Set<string>();
    let failure: unknown = null;
    for (const id of ids) {
      try {
        await this.api.deleteMessage(chatId, id);
        gone.add(id);
      } catch (err) {
        // One already gone is deleted all the same. Anything else is a real failure.
        if ((err as { status?: number }).status === 404) gone.add(id);
        else failure ??= err;
      }
    }
    this.setMessages(
      chatId,
      (this.state.messages[chatId] ?? []).filter((m) => !gone.has(m.id)),
    );
    await this.syncChat(chatId).catch(() => undefined);
    if (failure) throw failure;
  }

  /**
   * Your settings for a chat: muting, pinning, archiving, starring and read state. The change shows
   * at once and is put back if the server refuses it.
   */
  async setChatSettings(chatId: string, body: Parameters<Api["updateChatSettings"]>[1]): Promise<void> {
    const before = this.state.chats[chatId];
    if (before) {
      const { follow_space: _follow, ...local } = body;
      this.putChat({ ...before, ...local } as Chat);
    }
    try {
      this.putChat(await this.api.updateChatSettings(chatId, body));
    } catch (err) {
      const now = this.state.chats[chatId];
      if (before && now) this.putChat({ ...now, ...pick(before, Object.keys(body) as (keyof Chat)[]) });
      throw err;
    }
  }

  private putChat(chat: Chat) {
    this.prune(chat);
    const chats = { ...this.state.chats, [chat.id]: chat };
    const order = Object.values(chats)
      .sort(
        (a, b) => Number(b.pinned) - Number(a.pinned) || b.last_message_at.localeCompare(a.last_message_at),
      )
      .map((c) => c.id);
    this.set({ chats, chatOrder: order });
  }

  private async updatePreview(chat: Chat) {
    const last = chat.last_message;
    if (!last) return;
    let text: string;
    if (last.kind === "system") text = this.describeSystem(last.system ?? {}, chat);
    else {
      const result = await this.crypto.decrypt(last);
      text = result.ok ? previewOf(result.content) : previewOf(null, result.reason);
      if (!result.ok && result.reason === "missing_key" && this.state.previews[chat.id]) return;
      // An album says what it holds, from the photos and videos of it this device has.
      const album = result.ok && !last.deleted ? albumOf(result.content) : undefined;
      if (album && result.ok) {
        // By place in the album, so a copy still showing as sending is not counted twice.
        const kinds = new Map<number, "photo" | "video">();
        for (const m of this.state.messages[chat.id] ?? []) {
          const mark = m.deleted ? undefined : albumOf(m.content);
          if (mark?.id === album.id && m.senderId === last.sender_id)
            kinds.set(mark.index, (m.content as { kind: "photo" | "video" }).kind);
        }
        kinds.set(album.index, (result.content as { kind: "photo" | "video" }).kind);
        text = albumPreview(album.count, [...kinds.values()]);
      }
    }
    const previews = { ...this.state.previews, [chat.id]: text };
    this.set({ previews });
    await this.store.set("previews", previews);
  }

  describeSystem(s: Record<string, string>, chat?: Chat): string {
    const name = (id?: string) => {
      if (!id) return "Someone";
      if (id === this.state.me?.id) return "You";
      const m = chat?.members.find((x) => x.user.id === id);
      return m?.user.display_name.split(" ")[0] ?? "Someone";
    };
    const channel = chat?.kind === "channel";
    switch (s.event) {
      case "created":
        return channel ? `${name(s.actor)} created #${s.title}` : `${name(s.actor)} created the group`;
      case "renamed":
        return channel
          ? `${name(s.actor)} renamed the channel to #${s.title}`
          : `${name(s.actor)} renamed the group to ${s.title}`;
      case "channel_joined":
        return `${name(s.actor)} joined`;
      case "member_added":
        return `${name(s.actor)} added ${name(s.target)}`;
      case "member_removed":
        return `${name(s.actor)} removed ${name(s.target)}`;
      case "member_left":
        return `${name(s.actor)} left`;
      case "member_joined":
        return `${name(s.actor)} joined with an invite link`;
      case "role_changed":
        return s.role === "admin"
          ? `${name(s.target)} is now an admin`
          : `${name(s.target)} is no longer an admin`;
      case "assistant":
        return s.allowed === "off"
          ? `${name(s.actor)} turned off Queravel for this chat`
          : `${name(s.actor)} allowed Queravel in this chat`;
      case "pinned":
        return `${name(s.actor)} pinned a message`;
      case "shared":
        return `${name(s.actor)} added this channel to ${s.space}`;
      case "unshared":
        return `This channel is no longer shared with ${s.space}`;
      case "photo":
        return channel
          ? `${name(s.actor)} changed the channel photo`
          : `${name(s.actor)} changed the group photo`;
      case "backup_recovery":
        return "Rhema support started a recovery of your message history";
      case "disappearing":
        return s.seconds === "0"
          ? `${name(s.actor)} turned off disappearing messages`
          : `${name(s.actor)} turned on disappearing messages`;
      default:
        return "";
    }
  }

  async createDirect(userId: string): Promise<Chat> {
    const chat = await this.api.createDirect(userId);
    this.putChat(chat);
    return chat;
  }

  /** Your own chat, made the first time it is asked for. */
  async selfChat(): Promise<Chat> {
    const chat = await this.api.selfChat();
    this.putChat(chat);
    return chat;
  }

  async createGroup(title: string, memberIds: string[]): Promise<Chat> {
    const chat = await this.api.createGroup(title, memberIds);
    this.putChat(chat);
    return chat;
  }

  async removeMember(chatId: string, userId: string): Promise<void> {
    await this.api.removeMember(chatId, userId);
    // Members who leave must not be able to read what comes next.
    await this.crypto.rotate(chatId);
    await this.refreshChat(chatId);
  }

  /**
   * Leaves a group. This device forgets its outbound session, so nothing it sends if it is added
   * back reuses a key from before. Everyone who stays starts a new session with their next message,
   * because the member list they encrypt for no longer includes this person (see encrypt in
   * e2ee.ts), so nothing sent after this can be read with the keys this person holds.
   */
  async leaveGroup(chatId: string): Promise<void> {
    const me = this.state.me?.id;
    if (!me) throw new Error("Not signed in");
    await this.api.removeMember(chatId, me);
    await this.crypto.rotate(chatId).catch(() => undefined);
    await this.refreshChat(chatId);
  }

  // Delivery acks

  /** Notes that a message reached this device. Acks for many chats go out together shortly after. */
  private queueDelivered(chatId: string, seq: number) {
    if (seq <= (this.acks.get(chatId) ?? 0)) return;
    this.acks.set(chatId, seq);
    this.ackTimer ??= setTimeout(() => void this.flushDelivered(), ACK_DELAY);
  }

  /** Sends the waiting delivery acks. Ones that fail wait for the next. */
  async flushDelivered(): Promise<void> {
    if (this.ackTimer) clearTimeout(this.ackTimer);
    this.ackTimer = null;
    const waiting = [...this.acks.entries()].map(([chat_id, seq]) => ({ chat_id, seq }));
    this.acks.clear();
    for (let i = 0; i < waiting.length; i += ACK_BATCH) {
      const batch = waiting.slice(i, i + ACK_BATCH);
      try {
        await this.api.ackDelivered(batch);
      } catch {
        for (const a of batch) if (a.seq > (this.acks.get(a.chat_id) ?? 0)) this.acks.set(a.chat_id, a.seq);
      }
    }
  }

  /** Acks every chat with messages this device has not acked, after catching up. */
  private ackChats(chats: Chat[]) {
    const me = this.state.me?.id;
    for (const chat of chats) {
      // The list carries only the first few members of a big group. Without your own row there,
      // what you have read is still known, and delivery is never behind it.
      const mine = chat.members.find((m) => m.user.id === me);
      if (chat.last_seq > (mine?.delivered_seq ?? chat.read_seq)) this.queueDelivered(chat.id, chat.last_seq);
    }
  }

  /**
   * Forwards a message to other chats. Each copy is encrypted again for its chat. A file is sent
   * again from the upload it already has, so nothing is uploaded twice. A note, when given, follows
   * each copy as its own message.
   */
  async forward(message: ChatMessage, toChatIds: string[], note?: string): Promise<void> {
    const content = message.content;
    if (!content || message.deleted) throw new Error("This message cannot be forwarded.");
    let copy: Content;
    let forwarded: ForwardedFile[] | undefined;
    switch (content.type) {
      case "text": {
        // Mentions and replies belong to the chat the message came from. Formatting goes with
        // the words: the edit's, when it was edited.
        const format = message.edited !== null ? message.editedFormat : content.format;
        copy = {
          type: "text",
          body: message.edited ?? content.body,
          ...(format ? { format } : {}),
          forwarded: true,
        };
        break;
      }
      case "media":
        copy = {
          type: "media",
          kind: content.kind,
          // The same ref, version 2 chunking included, so the file is never uploaded again.
          media: content.media,
          ...((message.edited ?? content.caption) ? { caption: message.edited ?? content.caption } : {}),
          forwarded: true,
        };
        forwarded = [{ key: content.media.key, message_id: message.id }];
        break;
      case "location":
        copy = {
          type: "location",
          lat: content.lat,
          lng: content.lng,
          ...(content.title ? { title: content.title } : {}),
        };
        break;
      case "contact":
        copy = { ...content };
        break;
      default:
        throw new Error("This message cannot be forwarded.");
    }
    for (const chatId of toChatIds) {
      const pending = this.addOptimistic(chatId, copy);
      if (forwarded) {
        pending.forwarded = forwarded;
        this.patchLocal(chatId, pending.clientId, { forwardedFiles: forwarded });
      }
      await this.queue(pending);
      await this.deliver(pending);
      if (note?.trim()) await this.send(chatId, { type: "text", body: note.trim() });
    }
  }

  // Messages

  /** Loads cached messages instantly, then fetches anything newer. */
  async openChat(chatId: string): Promise<void> {
    if (!this.state.messages[chatId]) {
      const cached = sanitizeMessages(await this.store.get<unknown>(`msgs:${chatId}`));
      this.setMessages(chatId, [...cached, ...this.uploadMessages(chatId)]);
      if (!cached.length) {
        const page = await this.api.messages(chatId, { limit: PAGE });
        await this.ingest(chatId, page);
        this.set({ hasOlder: { ...this.state.hasOlder, [chatId]: page.length === PAGE } });
        return;
      }
    }
    await this.syncChat(chatId);
  }

  async loadOlder(chatId: string): Promise<void> {
    const first = this.state.messages[chatId]?.find((m) => m.seq > 0);
    if (!first) return;
    const page = await this.api.messages(chatId, { before: first.seq, limit: PAGE });
    await this.ingest(chatId, page);
    this.set({ hasOlder: { ...this.state.hasOlder, [chatId]: page.length === PAGE } });
  }

  /** Loads a thread's replies, and keeps them up to date while it is open. */
  async openThread(chatId: string, rootId: string): Promise<void> {
    const key = threadKey(chatId, rootId);
    if (!this.state.messages[key]) {
      const cached = sanitizeMessages(await this.store.get<unknown>(`msgs:${key}`));
      this.setMessages(key, [...cached, ...this.uploadMessages(key)]);
    }
    await this.syncThread(chatId, rootId);
  }

  /** Fetches the replies after the newest one held. Calls for the same thread are coalesced. */
  syncThread(chatId: string, rootId: string): Promise<void> {
    const key = threadKey(chatId, rootId);
    const running = this.syncing.get(key);
    if (running) return running.then(() => this.syncThread(chatId, rootId));
    const job = (async () => {
      for (;;) {
        const last = this.state.messages[key]?.reduce((n, m) => Math.max(n, m.seq), 0) ?? 0;
        const page = await this.api.messages(chatId, { thread: rootId, after: last, limit: 200 });
        await this.ingest(key, page);
        if (page.length < 200) break;
      }
    })().finally(() => this.syncing.delete(key));
    this.syncing.set(key, job);
    return job;
  }

  private applyThreadCount(chatId: string, rootId: string, count: number, lastAt: string | null) {
    const list = this.state.messages[chatId];
    if (!list?.some((m) => m.id === rootId)) return;
    this.setMessages(
      chatId,
      list.map((m) => (m.id === rootId ? { ...m, replyCount: count, lastReplyAt: lastAt } : m)),
    );
  }

  /** Fetches messages after the newest one we hold. Calls for the same chat are coalesced. */
  syncChat(chatId: string): Promise<void> {
    const running = this.syncing.get(chatId);
    if (running) return running.then(() => this.syncChat(chatId));
    const job = (async () => {
      if (!this.state.messages[chatId]) {
        await this.refreshChat(chatId);
        return;
      }
      for (;;) {
        const last = this.state.messages[chatId]?.reduce((n, m) => Math.max(n, m.seq), 0) ?? 0;
        const page = await this.api.messages(chatId, { after: last, limit: 200 });
        await this.ingest(chatId, page);
        if (page.length < 200) break;
      }
      await this.refreshChat(chatId);
      await this.acknowledge(chatId);
    })().finally(() => this.syncing.delete(chatId));
    this.syncing.set(chatId, job);
    return job;
  }

  /** Tells senders their messages reached this device. */
  private async acknowledge(chatId: string) {
    const chat = this.state.chats[chatId];
    const me = chat?.members.find((m) => m.user.id === this.state.me?.id);
    if (chat && me && chat.last_seq > me.delivered_seq)
      await this.api.markDelivered(chatId, chat.last_seq).catch(() => {});
  }

  private async ingest(chatId: string, page: ServerMessage[]) {
    if (!page.length) return;
    const list = [...(this.state.messages[chatId] ?? [])];
    const bySeq = new Map(list.filter((m) => m.seq > 0).map((m) => [m.seq, m]));
    for (const raw of page.sort((a, b) => a.seq - b.seq)) {
      if (bySeq.has(raw.seq)) continue;
      if (["message", "system", "vote", "notice", "import"].includes(raw.kind)) {
        const message = await this.decode(raw);
        // Replace our own optimistic copy, matched by client id.
        const local = list.findIndex((m) => m.seq === 0 && m.clientId && m.clientId === raw.client_id);
        if (local >= 0) list.splice(local, 1, message);
        else list.push(message);
        bySeq.set(raw.seq, message);
      } else {
        await this.applyRelation(list, raw);
        bySeq.set(raw.seq, { seq: raw.seq } as ChatMessage);
      }
    }
    list.sort((a, b) => (a.seq || Number.MAX_SAFE_INTEGER) - (b.seq || Number.MAX_SAFE_INTEGER));
    this.setMessages(chatId, list);
  }

  private async decode(raw: ServerMessage): Promise<ChatMessage> {
    const base: ChatMessage = {
      id: raw.id,
      clientId: raw.client_id,
      chatId: raw.chat_id,
      seq: raw.seq,
      senderId: raw.sender_id,
      createdAt: raw.created_at,
      kind: raw.kind === "system" ? "system" : "message",
      content: null,
      system: raw.system,
      edited: null,
      reactions: {},
      deleted: raw.deleted,
      status: "sent",
      expiresAt: raw.expires_at,
      threadRoot: raw.thread_root ?? null,
      replyCount: raw.reply_count ?? 0,
      lastReplyAt: raw.last_reply_at ?? null,
    };
    if (raw.kind === "system") return base;
    const result = await this.crypto.decrypt(raw);
    if (result.ok) base.content = result.content;
    else base.problem = result.reason;
    return base;
  }

  private async applyRelation(list: ChatMessage[], raw: ServerMessage) {
    const index = list.findIndex((m) => m.id === raw.relates_to);
    const current = list[index];
    if (!current) return;
    // Work on a copy so the previous state, which React may still hold, is never changed in place.
    const target: ChatMessage = { ...current };
    list[index] = target;
    if (raw.kind === "delete") {
      Object.assign(target, { deleted: true, content: null, problem: "deleted", reactions: {} });
      return;
    }
    const result = await this.crypto.decrypt(raw);
    if (!result.ok) return;
    const c = result.content;
    if (raw.kind === "edit" && c.type === "edit" && raw.sender_id === target.senderId) {
      target.edited = c.body;
      // A later edit without formatting is plain text, so the field is cleared rather than kept.
      if (c.format) target.editedFormat = c.format;
      else delete target.editedFormat;
      // An edit names everyone it mentions now, so the new names show as mentions too. For a
      // photo, video or file that is the people its caption mentions.
      if (c.mentions && (target.content?.type === "text" || target.content?.type === "media"))
        target.content = { ...target.content, mentions: c.mentions };
    }
    if (raw.kind === "reaction" && c.type === "reaction" && raw.sender_id) {
      const reactions = { ...target.reactions };
      for (const key of Object.keys(reactions))
        reactions[key] = reactions[key]!.filter((u) => u !== raw.sender_id);
      if (!c.remove) reactions[c.key] = [...(reactions[c.key] ?? []), raw.sender_id];
      for (const key of Object.keys(reactions)) if (!reactions[key]!.length) delete reactions[key];
      target.reactions = reactions;
    }
  }

  private async retryUndecrypted(chatId: string) {
    const list = this.state.messages[chatId];
    const chat = this.state.chats[chatId];
    if (chat) await this.updatePreview(chat);
    const waiting = (m: ChatMessage) => m.problem === "missing_key" || m.problem === "lost";
    if (!list?.some(waiting)) return;
    const missing = list.filter(waiting).map((m) => m.seq);
    const from = Math.min(...missing) - 1;
    const page = await this.api.messages(chatId, { after: from, limit: 200 });
    const next = [...list];
    for (const raw of page) {
      const i = next.findIndex((m) => m.seq === raw.seq && waiting(m));
      if (i >= 0) next[i] = await this.decode(raw);
    }
    this.setMessages(chatId, next);
  }

  private setMessages(chatId: string, list: ChatMessage[]) {
    const chat = this.state.chats[chatId];
    const withStatus = chat ? list.map((m) => this.withStatus(m, chat)) : list;
    this.set({ messages: { ...this.state.messages, [chatId]: withStatus } });
    void this.store.set(`msgs:${chatId}`, withStatus.filter((m) => m.seq > 0).slice(-CACHE_LIMIT));
  }

  private withStatus(m: ChatMessage, chat: Chat): ChatMessage {
    if (m.senderId !== this.state.me?.id || m.seq === 0) return m;
    const others = chat.members.filter((x) => x.user.id !== this.state.me?.id);
    if (!others.length) return { ...m, status: "sent" };
    const status: Delivery = others.every((x) => x.read_seq >= m.seq)
      ? "read"
      : others.every((x) => x.delivered_seq >= m.seq)
        ? "delivered"
        : "sent";
    return status === m.status ? m : { ...m, status };
  }

  private applyReceipt(r: { chat_id: string; user_id: string; read_seq: number; delivered_seq: number }) {
    const chat = this.state.chats[r.chat_id];
    if (!chat) return;
    const members = chat.members.map((m) =>
      m.user.id === r.user_id ? { ...m, read_seq: r.read_seq, delivered_seq: r.delivered_seq } : m,
    );
    // Reading a chat on another of your devices reads it here too, and ends Mark as unread. A
    // receipt that only says a message was delivered leaves the unread count alone.
    const read = r.user_id === this.state.me?.id && r.read_seq > chat.read_seq;
    const next = {
      ...chat,
      members,
      ...(read ? { unread: 0, read_seq: r.read_seq, marked_unread: false } : {}),
    };

    this.set({ chats: { ...this.state.chats, [chat.id]: next } });
    const list = this.state.messages[r.chat_id];
    if (list) this.setMessages(r.chat_id, list);
  }

  private applyTyping(t: { chat_id: string; user_id: string; active: boolean }) {
    const key = `${t.chat_id}:${t.user_id}`;
    clearTimeout(this.typingTimers.get(key));
    const current = new Set(this.state.typing[t.chat_id] ?? []);
    if (t.active) {
      current.add(t.user_id);
      this.typingTimers.set(
        key,
        setTimeout(() => this.applyTyping({ ...t, active: false }), TYPING_TTL),
      );
    } else current.delete(t.user_id);
    this.set({ typing: { ...this.state.typing, [t.chat_id]: [...current] } });
  }

  // Sending

  async send(
    chatId: string,
    content: Content,
    opts: { mentions?: string[]; threadRoot?: string } = {},
  ): Promise<void> {
    const pending = this.addOptimistic(chatId, content, opts);
    await this.queue(pending);
    await this.deliver(pending);
  }

  /** The chat with every member, which encrypting needs. The chat list carries only the first few. */
  private async fullChat(chatId: string): Promise<Chat | null> {
    const chat = this.state.chats[chatId];
    if (chat && chat.members.length >= chat.member_count) return chat;
    return this.refreshChat(chatId);
  }

  /**
   * Sends several messages that belong together, such as a question to Queravel and its answer.
   * They all show at once and reach the server in this order.
   */
  async sendAll(chatId: string, contents: Content[]): Promise<void> {
    const pending = contents.map((content) => this.addOptimistic(chatId, content));
    for (const p of pending) await this.queue(p);
    for (const [i, p] of pending.entries()) {
      if (await this.deliver(p)) continue;
      // Keep the order. What follows waits in the outbox and shows as failed until it goes out.
      for (const rest of pending.slice(i + 1)) this.markFailed(rest);
      return;
    }
  }

  /** Shows a message straight away, before it is encrypted and sent. */
  private addOptimistic(
    chatId: string,
    content: Content,
    opts: { mentions?: string[]; threadRoot?: string } = {},
  ): Pending {
    const clientId = uuid();
    const me = this.state.me!;
    const optimistic: ChatMessage = {
      id: `local:${clientId}`,
      clientId,
      chatId,
      seq: 0,
      senderId: me.id,
      createdAt: new Date().toISOString(),
      kind: "message",
      content,
      system: null,
      edited: null,
      reactions: {},
      deleted: false,
      status: "sending",
      expiresAt: null,
      threadRoot: opts.threadRoot ?? null,
    };
    const list = opts.threadRoot ? threadKey(chatId, opts.threadRoot) : chatId;
    this.setMessages(list, [...(this.state.messages[list] ?? []), optimistic]);
    if (opts.threadRoot)
      return {
        chatId,
        clientId,
        content,
        // A notice in a thread, such as "asked Queravel", is still a notice and never pushes.
        kind: content.type === "notice" ? "notice" : "message",
        mentions: opts.mentions,
        threadRoot: opts.threadRoot,
      };
    // Votes and notices are their own kinds, so the server neither counts them as unread nor
    // sends a push. It cannot read the content to tell.
    if (content.type === "vote")
      return { chatId, clientId, content, kind: "vote", relatesTo: content.poll_id };
    if (isNotice(content)) return { chatId, clientId, content, kind: "notice" };
    return { chatId, clientId, content, kind: "message", mentions: opts.mentions };
  }

  /**
   * Encrypts and uploads a file, then sends it as a media message. The message shows at once
   * with upload progress. Files upload two at a time and post in the order they were sent. If the
   * upload fails it is marked failed and retry starts it again. Resolves once the message has
   * posted, failed or been cancelled. A full storage plan rejects, so the screen can say so.
   */
  async sendMedia(chatId: string, file: Blob, opts: SendMediaOptions): Promise<void> {
    if (file.size > MAX_MEDIA_BYTES) throw new MediaTooLargeError(file.size);
    if (!this.state.me) throw new Error("Not signed in");
    await this.startUploads();
    const clientId = uuid();
    const done = this.whenUploadSettles(clientId);
    const key = listOf({ chatId, threadRoot: opts.threadRoot });
    const item = await this.uploads.add({
      id: clientId,
      chatId,
      file,
      kind: opts.kind,
      ...(opts.meta ? { meta: opts.meta } : {}),
      ...(opts.caption ? { caption: opts.caption } : {}),
      ...(opts.reply_to ? { reply_to: opts.reply_to } : {}),
      ...(opts.album ? { album: opts.album } : {}),
      ...(opts.mentions?.length ? { mentions: opts.mentions } : {}),
      ...(opts.threadRoot ? { threadRoot: opts.threadRoot } : {}),
    });
    if (!this.state.messages[key]?.some((m) => m.clientId === clientId && m.seq === 0)) {
      this.setMessages(key, [...(this.state.messages[key] ?? []), this.uploadMessage({ ...item, file })]);
    }
    await done;
  }

  /** Loads the uploads left from the last run, once. Their messages show when their chat opens. */
  private startUploads(): Promise<void> {
    this.uploadsStarted ??= this.uploads.start();
    return this.uploadsStarted;
  }

  private whenUploadSettles(clientId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const list = this.uploadWaiters.get(clientId) ?? [];
      list.push({ resolve, reject });
      this.uploadWaiters.set(clientId, list);
    });
  }

  private settleUpload(clientId: string, err?: unknown): void {
    const list = this.uploadWaiters.get(clientId);
    if (!list) return;
    this.uploadWaiters.delete(clientId);
    for (const w of list) {
      if (err) w.reject(err);
      else w.resolve();
    }
  }

  /** The message shown for a file still in the upload queue. */
  private uploadMessage(item: UploadView): ChatMessage {
    return {
      id: `local:${item.id}`,
      clientId: item.id,
      chatId: item.chatId,
      seq: 0,
      senderId: this.state.me?.id ?? null,
      createdAt: item.createdAt,
      kind: "message",
      content: uploadContent(item, item.ref),
      system: null,
      edited: null,
      reactions: {},
      deleted: false,
      status: item.status === "failed" ? "failed" : "sending",
      expiresAt: null,
      ...(item.ref || item.status === "failed" ? {} : { progress: item.progress ?? 0 }),
      ...(item.file ? { localFile: item.file } : {}),
      threadRoot: item.threadRoot ?? null,
    };
  }

  /** Messages for the files still uploading to one chat or thread, for when it first loads. */
  private uploadMessages(key: string): ChatMessage[] {
    return this.uploads
      .list()
      .filter((i) => listOf({ chatId: i.chatId, threadRoot: i.threadRoot }) === key)
      .map((i) => this.uploadMessage(i));
  }

  /** Keeps each upload's message in step with the queue: its progress, and failing. */
  private showUploads(items: UploadView[]): void {
    for (const item of items) {
      const key = listOf({ chatId: item.chatId, threadRoot: item.threadRoot });
      if (item.status === "failed") {
        this.uploadShown.delete(item.id);
        const local = this.state.messages[key]?.find((m) => m.clientId === item.id && m.seq === 0);
        if (local && local.status !== "failed") {
          this.patchLocal(key, item.id, { status: "failed", progress: undefined });
        }
        const err = this.uploadErrors.get(item.id);
        this.uploadErrors.delete(item.id);
        // A full storage plan needs the person to act, so the screen hears about it. Retry works
        // once they make room or upgrade.
        this.settleUpload(item.id, isPlanLimit(err) ? err : undefined);
        continue;
      }
      if (item.status !== "uploading" || item.progress === undefined) continue;
      const shown = this.uploadShown.get(item.id) ?? 0;
      // Redraw at most once a percent.
      if (item.progress - shown < 0.01 && item.progress < 1) continue;
      this.uploadShown.set(item.id, item.progress);
      this.patchLocal(key, item.id, { progress: item.progress });
    }
  }

  /** Posts the message for a file the queue has finished uploading. Runs in the order sent. */
  private async postUpload(item: QueuedUpload, media: MediaRef): Promise<void> {
    const key = listOf({ chatId: item.chatId, threadRoot: item.threadRoot });
    const content = uploadContent(item, media);
    this.uploadShown.delete(item.id);
    this.patchLocal(key, item.id, { content, progress: undefined });
    const pending: Pending = {
      chatId: item.chatId,
      clientId: item.id,
      content,
      kind: "message",
      attachments: [media.key],
      ...(item.mentions?.length ? { mentions: item.mentions } : {}),
      ...(item.threadRoot ? { threadRoot: item.threadRoot } : {}),
    };
    try {
      await this.queue(pending);
      await this.deliver(pending);
    } finally {
      // From here the outbox owns the message, and retry resends it from there.
      this.settleUpload(item.id);
    }
  }

  /**
   * Stops a file that is still uploading and takes its message off the screen. Answers false when
   * the upload has already finished, so the message is on its way.
   */
  cancelUpload(chatId: string, clientId: string, threadRoot?: string): boolean {
    const item = this.uploads.get(clientId);
    if (!item || item.ref) return false;
    void this.uploads.cancel(clientId);
    this.uploadShown.delete(clientId);
    this.uploadErrors.delete(clientId);
    const key = listOf({ chatId, threadRoot: threadRoot ?? item.threadRoot });
    const list = this.state.messages[key];
    if (list)
      this.set({
        messages: {
          ...this.state.messages,
          [key]: list.filter((m) => !(m.clientId === clientId && m.seq === 0)),
        },
      });
    this.settleUpload(clientId);
    return true;
  }

  /**
   * Changes a message that has not reached the server yet. Only the screen changes, not the store.
   * `key` is the list it shows in: the chat, or a thread.
   */
  private patchLocal(key: string, clientId: string, patch: Partial<ChatMessage>) {
    const list = this.state.messages[key];
    if (!list) return;
    this.set({
      messages: {
        ...this.state.messages,
        [key]: list.map((m) => (m.clientId === clientId && m.seq === 0 ? { ...m, ...patch } : m)),
      },
    });
  }

  async react(chatId: string, messageId: string, key: string, remove = false): Promise<void> {
    const pending: Pending = {
      chatId,
      clientId: uuid(),
      content: { type: "reaction", key, remove },
      kind: "reaction",
      relatesTo: messageId,
      ...this.threadOf(chatId, messageId),
    };
    await this.queue(pending);
    await this.deliver(pending);
  }

  /**
   * Replaces a message's text, or the caption of a photo, video or file. `format` is "md" when the
   * new body carries formatting marks. `mentions` are everyone the new text mentions. The server
   * tells only the people who were not mentioned before.
   */
  async edit(
    chatId: string,
    messageId: string,
    body: string,
    format?: TextFormat,
    mentions?: string[],
  ): Promise<void> {
    const pending: Pending = {
      chatId,
      clientId: uuid(),
      content: {
        type: "edit",
        body,
        ...(format ? { format } : {}),
        ...(mentions?.length ? { mentions } : {}),
      },
      kind: "edit",
      relatesTo: messageId,
      ...(mentions?.length ? { mentions } : {}),
      ...this.threadOf(chatId, messageId),
    };
    await this.queue(pending);
    await this.deliver(pending);
  }

  async deleteMessage(chatId: string, messageId: string): Promise<void> {
    const { threadRoot } = this.threadOf(chatId, messageId);
    await this.api.deleteMessage(chatId, messageId);
    if (threadRoot) await this.syncThread(chatId, threadRoot);
    else await this.syncChat(chatId);
  }

  /** The thread a message is a reply in, if it is one, for sending a reaction or edit to it. */
  private threadOf(chatId: string, messageId: string): { threadRoot?: string } {
    for (const [key, list] of Object.entries(this.state.messages)) {
      if (!key.startsWith(`${chatId}#`)) continue;
      const found = list.find((m) => m.id === messageId);
      if (found?.threadRoot) return { threadRoot: found.threadRoot };
    }
    return {};
  }

  private async queue(p: Pending) {
    const outbox = (await this.store.get<Pending[]>("outbox")) ?? [];
    // A retry replaces the queued copy rather than sending the same message twice.
    await this.store.set("outbox", [...outbox.filter((x) => x.clientId !== p.clientId), p]);
  }

  private async unqueue(clientId: string) {
    const outbox = (await this.store.get<Pending[]>("outbox")) ?? [];
    await this.store.set(
      "outbox",
      outbox.filter((p) => p.clientId !== clientId),
    );
  }

  private async deliver(p: Pending): Promise<boolean> {
    try {
      // The chat list carries only the first few members of each group. Encrypting needs all of
      // them, or the people left out never get the key.
      let chat: Chat | null | undefined = this.state.chats[p.chatId];
      if (!chat || chat.members.length < chat.member_count) chat = await this.refreshChat(p.chatId);
      if (!chat) throw new Error("chat gone");
      const envelope = await this.crypto.encrypt(chat, p.content);
      const sent = await this.api.sendMessage(p.chatId, {
        client_id: p.clientId,
        kind: p.kind,
        envelope,
        relates_to: p.relatesTo,
        // A reaction or edit to a reply is filed with its thread by the server.
        // A notice in a thread, such as "asked Queravel", goes in the thread too.
        ...(p.threadRoot && (p.kind === "message" || p.kind === "notice")
          ? { thread_root: p.threadRoot }
          : {}),
        mentions: p.mentions,
        ...(p.attachments?.length ? { attachments: p.attachments } : {}),
        ...(p.forwarded?.length ? { forwarded: p.forwarded } : {}),
      });
      await this.unqueue(p.clientId);
      await this.ingest(listOf(p), [sent]);
      if (p.threadRoot && p.kind === "message") {
        // Your own reply counts straight away. The server's count follows by thread.updated.
        const replies = (this.state.messages[listOf(p)] ?? []).filter(
          (m) => m.kind === "message" && m.seq > 0 && m.content?.type !== "notice",
        );
        const root = this.state.messages[p.chatId]?.find((m) => m.id === p.threadRoot);
        if (root && (root.replyCount ?? 0) < replies.length)
          this.applyThreadCount(p.chatId, p.threadRoot, replies.length, sent.created_at);
      }
      return true;
    } catch (err) {
      const status = (err as { status?: number }).status;
      // A 4xx will not succeed on retry, so it is dropped from the outbox. Anything else, such as
      // being offline, stays queued and goes out on reconnect. Either way the person sees it failed
      // and can retry, rather than watching a message sit at "sending".
      if (status && status >= 400 && status < 500) await this.unqueue(p.clientId);
      if (p.kind === "message" || p.kind === "notice") this.markFailed(p);
      return false;
    }
  }

  private markFailed(p: Pending) {
    const list = this.state.messages[listOf(p)];
    if (!list) return;
    this.setMessages(
      listOf(p),
      list.map((m) => (m.clientId === p.clientId && m.seq === 0 ? { ...m, status: "failed" } : m)),
    );
  }

  async retry(chatId: string, clientId: string, threadRoot?: string): Promise<void> {
    const key = threadRoot ? threadKey(chatId, threadRoot) : chatId;
    const list = this.state.messages[key] ?? [];
    const m = list.find((x) => x.clientId === clientId && x.seq === 0);
    if (!m?.content) return;
    if (this.uploads.get(clientId)?.status === "failed") {
      this.patchLocal(key, clientId, { status: "sending", progress: 0 });
      const done = this.whenUploadSettles(clientId);
      await this.uploads.retry(clientId);
      await done;
      return;
    }
    const mentions = m.content.type === "text" || m.content.type === "media" ? m.content.mentions : undefined;
    // A forward carries its files from the message it came from. Anything else uploaded its own.
    const attachments =
      !m.forwardedFiles && m.content.type === "media" && m.content.media.key
        ? [m.content.media.key]
        : undefined;
    const kind = isNotice(m.content) ? "notice" : "message";
    const p: Pending = {
      chatId,
      clientId,
      content: m.content,
      kind,
      mentions,
      attachments,
      ...(m.forwardedFiles ? { forwarded: m.forwardedFiles } : {}),
      ...(threadRoot ? { threadRoot } : {}),
    };
    this.setMessages(
      key,
      list.map((x) => (x === m ? { ...x, status: "sending" } : x)),
    );
    await this.queue(p);
    await this.deliver(p);
  }

  // Pins

  async loadPins(chatId: string): Promise<Pinned[]> {
    return this.putPins(chatId, await this.api.pins(chatId));
  }

  async pin(chatId: string, messageId: string): Promise<void> {
    this.putPins(chatId, await this.api.pin(chatId, messageId));
  }

  async unpin(chatId: string, messageId: string): Promise<void> {
    this.putPins(chatId, await this.api.unpin(chatId, messageId));
  }

  private async putPins(
    chatId: string,
    rows: { message: ServerMessage; pinned_by: string | null; pinned_at: string }[],
  ): Promise<Pinned[]> {
    const pins: Pinned[] = [];
    for (const row of rows) {
      // The copy in the timeline carries edits and reactions, so prefer it.
      const held = this.state.messages[chatId]?.find((m) => m.id === row.message.id);
      pins.push({
        message: held ?? (await this.decode(row.message)),
        pinnedBy: row.pinned_by,
        pinnedAt: row.pinned_at,
      });
    }
    this.set({ pins: { ...this.state.pins, [chatId]: pins } });
    return pins;
  }

  // Scheduled messages

  /**
   * Encrypts a message now and asks the server to post it at `sendAt`. People who join the chat
   * after it was scheduled may not be able to read it, as with any message sent before they joined.
   */
  async schedule(
    chatId: string,
    content: Content,
    sendAt: Date,
    opts: { mentions?: string[]; threadRoot?: string } = {},
  ): Promise<Scheduled> {
    let chat: Chat | null | undefined = this.state.chats[chatId];
    if (!chat || chat.members.length < chat.member_count) chat = await this.refreshChat(chatId);
    if (!chat) throw Object.assign(new Error("This chat is no longer available."), { status: 404 });
    const clientId = uuid();
    const envelope = await this.crypto.encrypt(chat, content);
    await this.store.set(`scheduled:${clientId}`, content);
    const row = await this.api.schedule(chatId, {
      client_id: clientId,
      envelope,
      send_at: sendAt.toISOString(),
      ...(opts.threadRoot ? { thread_root: opts.threadRoot } : {}),
      ...(opts.mentions?.length ? { mentions: opts.mentions } : {}),
    });
    const item = await this.scheduledOf(row);
    await this.loadScheduled().catch(() => undefined);
    return item;
  }

  async loadScheduled(): Promise<Scheduled[]> {
    const rows = await this.api.scheduled();
    const list = await Promise.all(rows.map((r) => this.scheduledOf(r)));
    this.set({ scheduled: list });
    return list;
  }

  private async scheduledOf(row: ServerScheduled): Promise<Scheduled> {
    return {
      id: row.id,
      chatId: row.chat_id,
      clientId: row.client_id,
      sendAt: row.send_at,
      threadRoot: row.thread_root,
      content: (await this.store.get<Content>(`scheduled:${row.client_id}`)) ?? null,
    };
  }

  async reschedule(id: string, sendAt: Date): Promise<void> {
    await this.api.reschedule(id, sendAt.toISOString());
    await this.loadScheduled();
  }

  async cancelScheduled(id: string): Promise<void> {
    const item = this.state.scheduled?.find((s) => s.id === id);
    await this.api.cancelScheduled(id);
    if (item) await this.store.delete(`scheduled:${item.clientId}`);
    await this.loadScheduled();
  }

  async sendScheduledNow(id: string): Promise<void> {
    const item = this.state.scheduled?.find((s) => s.id === id);
    await this.api.sendScheduledNow(id);
    await this.loadScheduled();
    if (item) await this.syncChat(item.chatId).catch(() => undefined);
  }

  // Reminders

  async remind(chatId: string, messageId: string, at: Date): Promise<void> {
    await this.api.remind(chatId, messageId, at.toISOString());
    await this.loadReminders();
  }

  /** Loads the invites the server texted for you, which `invites.updated` keeps current after. */
  async loadSmsInvites(): Promise<SmsInvite[]> {
    const list = await this.api.sentSmsInvites();
    this.set({ smsInvites: list });
    return list;
  }

  async loadReminders(done = false): Promise<Reminder[]> {
    const rows = await this.api.reminders(done);
    const list: Reminder[] = [];
    for (const r of rows) {
      const held = this.state.messages[r.chat_id]?.find((m) => m.id === r.message.id);
      list.push({
        id: r.id,
        chatId: r.chat_id,
        message: held ?? (await this.decode(r.message)),
        remindAt: r.remind_at,
        notified: r.notified,
        done: r.done,
      });
    }
    if (!done) this.set({ reminders: list });
    return list;
  }

  async completeReminder(id: string, done = true): Promise<void> {
    await this.api.updateReminder(id, { done });
    await this.loadReminders();
  }

  async snoozeReminder(id: string, at: Date): Promise<void> {
    await this.api.updateReminder(id, { remind_at: at.toISOString() });
    await this.loadReminders();
  }

  async deleteReminder(id: string): Promise<void> {
    await this.api.deleteReminder(id);
    await this.loadReminders();
  }

  // Status and quiet hours

  async setStatus(emoji: string, text: string, until: Date | null): Promise<void> {
    this.set({ me: await this.api.setStatus({ emoji, text, until: until?.toISOString() ?? null }) });
  }

  async clearStatus(): Promise<void> {
    this.set({ me: await this.api.clearStatus() });
  }

  async setQuietHours(hours: QuietHours | null): Promise<void> {
    this.set({ me: hours ? await this.api.setQuietHours(hours) : await this.api.clearQuietHours() });
  }

  /** Someone changed their profile or status. Updates them in every chat they are in. */
  private async refreshUser(userId: string): Promise<void> {
    const held = Object.values(this.state.chats).some((c) => c.members.some((m) => m.user.id === userId));
    if (!held) return;
    const user = await this.api.user(userId).catch(() => null);
    if (!user) return;
    const chats: Record<string, Chat> = {};
    for (const [id, c] of Object.entries(this.state.chats))
      chats[id] = c.members.some((m) => m.user.id === userId)
        ? { ...c, members: c.members.map((m) => (m.user.id === userId ? { ...m, user } : m)) }
        : c;
    this.set({ chats });
  }

  // Search. The server cannot read messages, so search runs over what this device has decrypted.

  async search(query: string, opts: { chatId?: string; limit?: number } = {}): Promise<SearchHit[]> {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    const lists = new Map<string, ChatMessage[]>();
    const keys = opts.chatId ? [`msgs:${opts.chatId}`] : await this.store.keys("msgs:");
    for (const key of keys) {
      const id = key.slice("msgs:".length);
      lists.set(id, this.state.messages[id] ?? sanitizeMessages(await this.store.get<unknown>(key)));
    }
    for (const [id, list] of Object.entries(this.state.messages))
      if (!opts.chatId || id === opts.chatId || id.startsWith(`${opts.chatId}#`)) lists.set(id, list);
    const hits: SearchHit[] = [];
    const seen = new Set<string>();
    for (const [key, list] of lists) {
      const chatId = key.split("#")[0]!;
      if (!this.state.chats[chatId]) continue;
      if (opts.chatId && chatId !== opts.chatId) continue;
      for (const m of list) {
        if (m.kind !== "message" || seen.has(m.id)) continue;
        const text = searchableText(m);
        const lower = text.toLowerCase();
        if (!text || !words.every((w) => lower.includes(w))) continue;
        seen.add(m.id);
        hits.push({ chatId, message: m, text });
      }
    }
    hits.sort((a, b) => b.message.createdAt.localeCompare(a.message.createdAt));
    return hits.slice(0, opts.limit ?? 50);
  }

  // Space admin

  allChannels(spaceId: string): Promise<ChannelInfo[]> {
    return this.api.allChannels(spaceId);
  }

  async updateChannel(
    spaceId: string,
    chatId: string,
    body: { private?: boolean; auto_join?: boolean },
  ): Promise<ChannelInfo> {
    const out = await this.api.updateChannel(spaceId, chatId, body);
    await this.refreshChat(chatId);
    return out;
  }

  async deleteChannel(spaceId: string, chatId: string): Promise<void> {
    await this.api.deleteChannel(spaceId, chatId);
    await this.refreshChat(chatId);
  }

  async updateSpaceApps(spaceId: string, apps: Partial<Record<AppName, boolean>>): Promise<void> {
    const space = await this.api.updateSpace(spaceId, { apps });
    this.set({ spaces: this.state.spaces.map((s) => (s.id === space.id ? space : s)) });
  }

  // Space workflows. The server cannot write encrypted messages, so this person's devices encrypt
  // every message their workflows post. See apps/server/src/rhema/routes/workflows.py.

  async loadWorkflows(spaceId: string): Promise<Workflow[]> {
    const rows = await this.api.workflows(spaceId);
    const list = await Promise.all(rows.map((r) => this.workflowOf(r)));
    this.set({ workflows: { ...this.state.workflows, [spaceId]: list } });
    return list;
  }

  private async workflowOf(row: ServerWorkflow): Promise<Workflow> {
    return {
      id: row.id,
      spaceId: row.space_id,
      chatId: row.chat_id,
      kind: row.kind,
      name: row.name,
      ownerId: row.owner_id,
      text: await this.workflowText(row),
      repeat: row.repeat,
      weekday: row.weekday,
      minute: row.minute,
      tz: row.tz,
      paused: row.paused,
      problem: row.problem,
      nextRunAt: row.next_run_at,
      queued: row.queued,
      queuedUntil: row.queued_until,
      waiting: row.waiting,
      lastRunAt: row.last_run_at,
    };
  }

  /**
   * Decrypts a workflow's text. The text is encrypted for the channel but never posted, so reading
   * it cannot clash with a real message's place in the Megolm session.
   */
  private async workflowText(row: ServerWorkflow): Promise<string | null> {
    const result = await this.crypto.decrypt({
      id: `workflow:${row.id}`,
      chat_id: row.chat_id,
      seq: 0,
      kind: "message",
      sender_id: row.owner_id,
      sender_device_id: row.definition.device_id,
      client_id: null,
      envelope: row.definition,
      relates_to: null,
      system: null,
      created_at: row.updated_at,
      deleted: false,
      expires_at: null,
    });
    return result.ok && result.content.type === "text" ? result.content.body : null;
  }

  private async encryptFor(chatId: string, content: Content) {
    const chat = await this.fullChat(chatId);
    if (!chat) throw Object.assign(new Error("Join the channel to write its workflows."), { status: 403 });
    return { chat, envelope: await this.crypto.encrypt(chat, content) };
  }

  async createWorkflow(spaceId: string, input: WorkflowInput): Promise<Workflow> {
    const { envelope } = await this.encryptFor(input.chatId, { type: "text", body: input.text });
    const row = await this.api.createWorkflow(spaceId, {
      chat_id: input.chatId,
      kind: input.kind,
      name: input.name,
      definition: envelope,
      ...(input.schedule ?? {}),
    });
    const wf = await this.workflowOf(row);
    await this.loadWorkflows(spaceId).catch(() => undefined);
    void this.runWorkflows();
    return wf;
  }

  /** Changes a workflow. New text is encrypted here, and this person becomes its owner. */
  async updateWorkflow(
    spaceId: string,
    id: string,
    change: { name?: string; text?: string; schedule?: WorkflowSchedule; paused?: boolean },
  ): Promise<void> {
    const wf = this.state.workflows[spaceId]?.find((w) => w.id === id);
    const definition =
      change.text !== undefined && wf
        ? (await this.encryptFor(wf.chatId, { type: "text", body: change.text })).envelope
        : undefined;
    await this.api.updateWorkflow(spaceId, id, {
      ...(change.name !== undefined ? { name: change.name } : {}),
      ...(definition ? { definition } : {}),
      ...(change.schedule ?? {}),
      ...(change.paused !== undefined ? { paused: change.paused } : {}),
    });
    await this.loadWorkflows(spaceId);
    void this.runWorkflows();
  }

  async deleteWorkflow(spaceId: string, id: string): Promise<void> {
    await this.api.deleteWorkflow(spaceId, id);
    await this.loadWorkflows(spaceId);
  }

  private workflowsRunning: Promise<void> | null = null;
  private workflowsAgain = false;

  /**
   * Does what this person's workflows need from this device: welcomes people who joined, and
   * encrypts the next runs of recurring ones, one ciphertext per run, so no two posts share a
   * message index. Runs again if asked while running.
   */
  runWorkflows(): Promise<void> {
    if (this.workflowsRunning) {
      this.workflowsAgain = true;
      return this.workflowsRunning;
    }
    this.workflowsRunning = (async () => {
      try {
        do {
          this.workflowsAgain = false;
          await this.workflowWork();
        } while (this.workflowsAgain);
      } catch {
        // Offline, or signed out. The server asks again when it next needs this device.
      } finally {
        this.workflowsRunning = null;
      }
    })();
    return this.workflowsRunning;
  }

  private async workflowWork(): Promise<void> {
    if (!this.crypto) return;
    const work = await this.api.workflowWork();
    for (const item of work.items) {
      const wf = item.workflow;
      try {
        const text = await this.workflowText(wf);
        if (text === null) continue;
        // Members may have changed since the chat was loaded, and the key must reach them all.
        const chat = await this.refreshChat(wf.chat_id);
        if (!chat) continue;
        if (item.welcome.length) {
          // The newcomers' devices must get the key, so look them up afresh.
          for (const id of item.welcome) this.crypto.forgetDevices(id);
          const names = item.welcome.flatMap((id) => {
            const name = chat.members.find((m) => m.user.id === id)?.user.display_name;
            return name ? [`@${name}`] : [];
          });
          const content: Content = {
            type: "text",
            body: [names.join(" "), text].filter(Boolean).join(" "),
            mentions: item.welcome,
            workflow: { id: wf.id, kind: "welcome", name: wf.name },
          };
          const envelope = await this.crypto.encrypt(chat, content);
          await this.api.postWelcome(wf.id, { client_id: uuid(), envelope, user_ids: item.welcome });
          if (this.state.messages[chat.id]) await this.syncChat(chat.id).catch(() => undefined);
        } else if (item.runs.length) {
          const content: Content = {
            type: "text",
            body: text,
            workflow: { id: wf.id, kind: "recurring", name: wf.name },
          };
          const runs = [];
          for (const at of item.runs)
            runs.push({ client_id: uuid(), send_at: at, envelope: await this.crypto.encrypt(chat, content) });
          await this.api.putWorkflowRuns(wf.id, {
            as_of: work.as_of,
            runs,
            replace: item.replace,
            // Admins who joined since can then read the text too.
            ...(item.replace
              ? { definition: await this.crypto.encrypt(chat, { type: "text", body: text }) }
              : {}),
          });
        }
      } catch {
        // One workflow failing leaves the rest to run. The server asks again later.
      }
    }
  }

  /** Adds a channel another space shared into one of yours. You join it there. */
  async acceptShare(code: string, spaceId: string): Promise<Chat> {
    const chat = await this.api.acceptShare(code, spaceId);
    this.putChat(chat);
    void this.refreshSpaces().catch(() => undefined);
    return chat;
  }

  /**
   * Stops sharing a channel with a space. People who were in it only through that space leave it,
   * so the next message from anyone still in it goes out with new keys, as after a leave.
   */
  async stopSharing(spaceId: string, chatId: string, otherSpaceId: string): Promise<void> {
    await this.api.stopSharing(spaceId, chatId, otherSpaceId);
    await this.crypto.rotate(chatId).catch(() => undefined);
    await this.refreshChat(chatId);
  }

  // Imported chat history. See import/queue.ts, which drives these.

  /** The device's local store, for features that keep their own state beside the client's. */
  get localStore(): Store {
    return this.store;
  }

  /** Ids of every message already imported into a chat, by anyone, so an import adds only what is new. */
  async importedIds(chatId: string): Promise<Set<string>> {
    const ids = new Set<string>();
    let after = 0;
    for (;;) {
      const page = await this.api.messages(chatId, { after, limit: 200, kind: "import" });
      for (const raw of page) {
        const result = await this.crypto.decrypt(raw);
        if (result.ok && result.content.type === "imported")
          for (const m of result.content.messages) ids.add(m.id);
      }
      if (page.length < 200) return ids;
      after = page[page.length - 1]!.seq;
    }
  }

  /** Encrypts and uploads one file from an imported chat. A full storage plan throws a 402. */
  uploadImportMedia(chatId: string, file: Blob, meta: FileMeta): Promise<MediaRef> {
    if (file.size > MAX_MEDIA_BYTES) return Promise.reject(new MediaTooLargeError(file.size));
    return file.arrayBuffer().then((bytes) => this.media.send(chatId, bytes, meta, undefined, "import"));
  }

  /**
   * Sends one batch of imported history. It goes out as its own kind, so it neither pushes nor counts
   * as unread, and the same client id on a retry returns the batch already stored.
   */
  async sendImport(
    chatId: string,
    content: Extract<Content, { type: "imported" }>,
    opts: { clientId: string; attachments: string[] },
  ): Promise<void> {
    let chat: Chat | null | undefined = this.state.chats[chatId];
    if (!chat || chat.members.length < chat.member_count) chat = await this.refreshChat(chatId);
    if (!chat) throw Object.assign(new Error("This chat is no longer available."), { status: 404 });
    const envelope = await this.crypto.encrypt(chat, content);
    const sent = await this.api.sendMessage(chatId, {
      client_id: opts.clientId,
      kind: "import",
      envelope,
      ...(opts.attachments.length ? { attachments: opts.attachments } : {}),
    });
    if (this.state.messages[chatId]) await this.ingest(chatId, [sent]);
  }

  async flushOutbox(): Promise<void> {
    const outbox = (await this.store.get<Pending[]>("outbox")) ?? [];
    for (const p of outbox) await this.deliver(p);
  }

  async markRead(chatId: string): Promise<void> {
    const chat = this.state.chats[chatId];
    const list = this.state.messages[chatId];
    const last = list?.reduce((n, m) => Math.max(n, m.seq), 0) ?? 0;
    // Opening a chat the person marked unread clears the mark, even with nothing new in it.
    if (!chat || (last <= chat.read_seq && !chat.marked_unread)) return;
    const seq = Math.max(last, chat.read_seq);
    this.set({
      chats: {
        ...this.state.chats,
        [chatId]: { ...chat, unread: 0, mentioned: false, read_seq: seq, marked_unread: false },
      },
    });
    await this.api.markRead(chatId, seq);
  }

  typing(chatId: string, active = true): void {
    const now = Date.now();
    if (active && now - (this.lastTypingSent.get(chatId) ?? 0) < 3000) return;
    this.lastTypingSent.set(chatId, active ? now : 0);
    this.socket?.emit("typing", { chat_id: chatId, active });
  }

  // Calls. See calls.ts for how the media key is shared.

  startCall(chatId: string, media: CallMedia): Promise<ActiveCall> {
    return this.calls.startCall(chatId, media);
  }

  joinCall(callId: string): Promise<ActiveCall> {
    return this.calls.joinCall(callId);
  }

  leaveCall(): Promise<void> {
    return this.calls.leaveCall();
  }

  declineCall(callId: string): Promise<void> {
    return this.calls.declineCall(callId);
  }

  callHistory(): Promise<CallInfo[]> {
    return this.calls.callHistory();
  }
}
